import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {getDiseaseTests, createDiseaseTest, updateDiseaseTest} from "../../api/pond";
import {useTranslation} from "react-i18next";

interface DiseaseTabProps {
  pondId: string;
  cycleId?: string;
  isCycleActive?: boolean;
}

const DiseaseTab: React.FC<DiseaseTabProps> = ({pondId, cycleId, isCycleActive = true}) => {
  const {t} = useTranslation();
  const [diseaseTests, setDiseaseTests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTest, setEditingTest] = useState<any>(null);
  const [formData, setFormData] = useState({
    date: "",
    diseaseType: "",
    laboratory: "",
    result: "negative",
    remark: "",
  });

  useEffect(() => {
    fetchDiseaseTests();
  }, [pondId, cycleId]);

  const fetchDiseaseTests = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDiseaseTests(pondId, cycleId);
      setDiseaseTests(data.data || data);
    } catch (err) {
      setError("Failed to load disease tests");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (test?: any) => {
    if (test) {
      setEditingTest(test);
      setFormData({
        date: test.date ? new Date(test.date).toISOString().split("T")[0] : "",
        diseaseType: test.diseaseType || "",
        laboratory: test.laboratory || "",
        result: test.result || "negative",
        remark: test.remark || "",
      });
    } else {
      setEditingTest(null);
      setFormData({
        date: new Date().toISOString().split("T")[0],
        diseaseType: "",
        laboratory: "",
        result: "negative",
        remark: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingTest(null);
  };

  const handleSave = async () => {
    try {
      if (editingTest) {
        await updateDiseaseTest(editingTest.id, formData);
      } else {
        await createDiseaseTest(pondId, formData);
      }
      handleCloseModal();
      fetchDiseaseTests();
    } catch (err) {
      setError("Failed to save disease test");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{t("Disease Testing Records")}</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenModal()} disabled={!isCycleActive}>
          {t("Add Disease Test")}
        </Button>
      </Box>
      {!isCycleActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("Viewing closed cycle data. Editing is disabled.")}
        </Alert>
      )}
      <TableContainer component={Paper} sx={{overflowX: "auto", maxWidth: "100%"}}>
        <Table size="small" sx={{minWidth: 600}}>
          <TableHead>
            <TableRow>
              <TableCell>{t("Date")}</TableCell>
              <TableCell>{t("Test Type")}</TableCell>
              <TableCell>{t("Result")}</TableCell>
              <TableCell>{t("Sample Size")}</TableCell>
              <TableCell>{t("Positive Cases")}</TableCell>
              <TableCell>{t("Remark")}</TableCell>
              <TableCell>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {diseaseTests.map((test) => (
              <TableRow key={test.id}>
                <TableCell>{test.date ? new Date(test.date).toLocaleDateString() : "-"}</TableCell>
                <TableCell>{test.diseaseType}</TableCell>
                <TableCell>{test.result}</TableCell>
                <TableCell>{test.sampleSize}</TableCell>
                <TableCell>{test.positiveCases}</TableCell>
                <TableCell sx={{maxWidth: 150, wordWrap: "break-word"}}>{test.remark || ""}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => handleOpenModal(test)} disabled={!isCycleActive}>
                    {t("Edit")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>{editingTest ? t("Edit Disease Test") : t("Add Disease Test")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t("Date")}
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              InputLabelProps={{shrink: true}}
            />
            <TextField
              label={t("Disease Type")}
              value={formData.diseaseType}
              onChange={(e) => setFormData({...formData, diseaseType: e.target.value})}
              placeholder={t("e.g., WSSV, EMS, EHP")}
            />
            <TextField
              label={t("Laboratory")}
              value={formData.laboratory}
              onChange={(e) => setFormData({...formData, laboratory: e.target.value})}
              placeholder={t("Laboratory name")}
            />
            <FormControl>
              <InputLabel>{t("Result")}</InputLabel>
              <Select value={formData.result} onChange={(e) => setFormData({...formData, result: e.target.value})} label={t("Result")}>
                <MenuItem value="negative">{t("Negative")}</MenuItem>
                <MenuItem value="positive">{t("Positive")}</MenuItem>
                <MenuItem value="unknown">{t("Unknown")}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t("Notes")}
              multiline
              rows={3}
              value={formData.remark}
              onChange={(e) => setFormData({...formData, remark: e.target.value})}
              placeholder={t("Additional details about the test and results")}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t("Cancel")}</Button>
          <Button onClick={handleSave} variant="contained">
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiseaseTab;
